<template>
    <div>
        <Modal size="md" @close="$emit('close')">
            <template #header>
                <div class="text-gray-600">{{ $t("vue.new_domain") }}</div>
            </template>

            <template #body>
                <div v-if="!domainAdded" class="px-6 pb-6">
                    <TextInput
                        v-model="v$.domain.$model"
                        class="mb-6 mt-6 w-full md:w-[470px]"
                        :placeholder="$t('vue.enter_your_domain')"
                        :label="$t('vue.domain')"
                        :error="getError('domain')"
                        required
                    >
                        <template #help>
                            {{ $t("vue.e_g_example_com") }}
                        </template>
                    </TextInput>
                    <div
                        v-if="hasScalePlan"
                        class="mt-10 rounded-md bg-[#FFE9A7] p-3 text-sm"
                    >
                        <div class="flex items-center gap-2 font-bold">
                            <ExclamationTriangleIcon class="h-4 w-4" />
                            <h3 class="font-bold">
                                {{ $t("vue.billing_update") }}
                            </h3>
                        </div>
                        <p v-if="paidMonthly" class="mt-2 text-gray-800">
                            {{ $t("vue.billing_update_monthly_message") }}
                        </p>
                        <p v-if="paidYearly" class="mt-2 text-gray-800">
                            {{ $t("vue.billing_update_yearly_message") }}
                        </p>
                    </div>
                </div>

                <div v-if="domainAdded" class="p-6">
                    <div
                        class="relative mt-3 flex rounded bg-yellow-100 pb-[13px] pl-3.5 pr-6 pt-3"
                    >
                        <svg
                            class="shrink-0"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M8.25685 3.09898C9.02185 1.73898 10.9788 1.73898 11.7428 3.09898L17.3228 13.019C18.0728 14.353 17.1098 15.999 15.5808 15.999H4.41985C2.88985 15.999 1.92685 14.353 2.67685 13.019L8.25685 3.09898ZM10.9998 13C10.9998 13.2652 10.8945 13.5196 10.707 13.7071C10.5194 13.8946 10.2651 14 9.99985 14C9.73463 14 9.48028 13.8946 9.29274 13.7071C9.10521 13.5196 8.99985 13.2652 8.99985 13C8.99985 12.7348 9.10521 12.4804 9.29274 12.2929C9.48028 12.1053 9.73463 12 9.99985 12C10.2651 12 10.5194 12.1053 10.707 12.2929C10.8945 12.4804 10.9998 12.7348 10.9998 13ZM9.99985 4.99998C9.73463 4.99998 9.48028 5.10534 9.29274 5.29287C9.10521 5.48041 8.99985 5.73476 8.99985 5.99998V8.99998C8.99985 9.2652 9.10521 9.51955 9.29274 9.70709C9.48028 9.89462 9.73463 9.99998 9.99985 9.99998C10.2651 9.99998 10.5194 9.89462 10.707 9.70709C10.8945 9.51955 10.9998 9.2652 10.9998 8.99998V5.99998C10.9998 5.73476 10.8945 5.48041 10.707 5.29287C10.5194 5.10534 10.2651 4.99998 9.99985 4.99998Z"
                                fill="#D69E2E"
                            />
                        </svg>
                        <div class="pl-3.5 text-sm leading-normal">
                            <p class="font-medium text-yellow-800">
                                {{ $t("vue.domain_added") }}
                            </p>
                            <p class="mt-[7px] text-yellow-700">
                                {{ $t("vue.update_dns_records_message") }}
                            </p>
                        </div>
                    </div>

                    <div class="relative">
                        <textarea
                            ref="txtRecord"
                            style="resize: none"
                            readonly
                            rows="2"
                            class="mt-5 block w-full rounded-md border border-gray-300 bg-white pb-2.5 pl-3 pr-[22px] pt-2 text-sm leading-normal text-gray-800 placeholder-gray-400 shadow-sm transition duration-100 ease-in-out hover:bg-gray-50 focus:border-2 focus:border-gray-600 focus:pb-[9px] focus:pl-[11px] focus:pr-[21px] focus:pt-[7px] focus:outline-hidden md:w-[470px]"
                            v-text="txtRecordValue"
                        ></textarea>

                        <transition
                            enter-active-class="transition duration-100 ease-out transform"
                            enter-from-class="scale-95 opacity-0"
                            enter-to-class="scale-100 opacity-100"
                            leave-active-class="transition duration-75 ease-in transform"
                            leave-from-class="scale-100 opacity-100"
                            leave-to-class="scale-95 opacity-0"
                        >
                            <Tooltip
                                v-if="copiedSuccess"
                                class="absolute top-[-55px]"
                                style="left: 50%; transform: translate(-50%, 0)"
                            >
                                {{ $t("vue.copied_to_clipboard") }}
                            </Tooltip>
                        </transition>
                    </div>

                    <AtomicButton
                        class="relative mt-5"
                        color="white"
                        size="sm"
                        @click="copyTextArea"
                    >
                        <template #icon>
                            <DocumentDuplicateIcon class="mr-2 h-5 w-5" />
                        </template>
                        {{ $t("vue.copy_to_clipboard") }}
                    </AtomicButton>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-between border-t border-gray-200 p-6">
                    <AtomicButton
                        color="white"
                        size="md"
                        @click="$emit('close')"
                    >
                        {{ $t("vue.cancel") }}
                    </AtomicButton>
                    <AtomicButton
                        v-if="!domainAdded"
                        size="md"
                        color="yellow"
                        :disabled="loading || v$.$invalid"
                        @click="addDomain"
                    >
                        {{ $t("vue.add_domain") }}
                    </AtomicButton>

                    <AtomicButton
                        v-if="domainAdded"
                        size="md"
                        color="yellow"
                        :disabled="loading || v$.$invalid"
                        @click="$emit('close')"
                    >
                        {{ $t("vue.finish") }}
                    </AtomicButton>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script lang="ts" setup>
import useVuelidate from "@vuelidate/core";
import { required, maxLength } from "@vuelidate/validators";
import route from "ziggy-js";
import { create } from "@/composables/useNotifications";
import Tooltip from "@/components/Atomic/Miscellaneous/Tooltip.vue";
import type IInputErrors from "@/types/Atomic/IInputErrors";
import { DocumentDuplicateIcon } from "@heroicons/vue/24/outline";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import { useTranslations } from "@/composables/useTranslations";

const emit = defineEmits(["close", "created"]);

// Get access to translations
const { t } = useTranslations();

const state = reactive({
    domain: "",
});

const rules = {
    domain: {
        required,
        maxLength: maxLength(255),
    },
};

const v$ = useVuelidate(rules, state);

const globalProps = computed(() => usePage<IRhizomPageProps>().props);

const txtRecordValue = ref(
    `v=DMARC1; p=none; rua=mailto:${globalProps.value.team.uuid}@rua.dmarceye.com; fo=1; ri=86400;`,
);
const errors = ref<IInputErrors>({});
const loading = ref(false);
const domainAdded = ref(false);
const copiedSuccess = ref(false);

const txtRecord = ref();

const addDomain = () => {
    v$.value.$touch();
    if (v$.value.$error) {
        return;
    } else {
        router.post(
            route("domains.store"),
            {
                domain: state.domain,
            },
            {
                onBefore: () => {
                    loading.value = true;
                },
                onSuccess: (response) => {
                    // Check if we have the expected data structure
                    if (
                        response.props?.inactiveDomains?.[state.domain]
                            ?.recordToAdd
                    ) {
                        txtRecordValue.value =
                            response.props.inactiveDomains[
                                state.domain
                            ].recordToAdd;
                    } else {
                        // Fallback to default record
                        txtRecordValue.value = `v=DMARC1; p=none; rua=mailto:${response.props?.team?.uuid || "TEAM"}@rua.dmarceye.com; fo=1; ri=86400;`;
                    }

                    domainAdded.value = true;
                    emit("created");
                    create({
                        title: t("vue.domain_added"),
                        text: "",
                        type: "success",
                    });
                },
                onError: (err) => {
                    errors.value = err;
                    create({
                        title: t("vue.domain_not_added"),
                        text: t("vue.error_adding_domain"),
                        type: "error",
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            },
        );
    }
};
const copyTextArea = () => {
    let element = txtRecord.value as HTMLInputElement;
    element.select();
    document.execCommand("copy");

    copiedSuccess.value = true;
    setTimeout(() => {
        copiedSuccess.value = false;
    }, 1000);
};

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || errors.value?.[field] || "";
};

const hasScalePlan = computed(() => globalProps.value.hasScalePlan);
const paidMonthly = computed(() => globalProps.value.paidMonthly);
const paidYearly = computed(() => globalProps.value.paidYearly);
</script>
